import styled from "@emotion/styled";
import CancelIcon from "@mui/icons-material/Cancel";
import CloseIcon from "@mui/icons-material/Close";
import SearchIcon from "@mui/icons-material/Search";
import {
  Box,
  Button,
  Checkbox,
  FormControlLabel,
  Grid,
  IconButton,
  InputAdornment,
  Switch,
  Typography,
  CircularProgress,
} from "@mui/material";
import { useMutation, useQuery } from "@tanstack/react-query";
import React, { useState, useEffect } from "react";
import Carousel from "react-multi-carousel";
import "react-multi-carousel/lib/styles.css";
import { useNavigate } from "react-router-dom";
import Cookies from "universal-cookie";
import {
  getWithQueryApiServices,
  postWithQueryApiServices,
} from "../../../apis/api";
import { apiRoutes } from "../../../apis/apiPath";
import { filerSearchInit } from "../../../constants";
import { ROUTE_PATHS } from "../../../routes/routePath";
import { ListBox } from "../../../styles/commonStyles";
import { errorToast } from "../../../utils/helper";
import { CommonSearch } from "../../shared/CommonSearch";
import { CustomTextField } from "../../shared/CustomTextField";

const responsive = {
  desktop: {
    breakpoint: { max: 3000, min: 1024 },
    items: 3,
    slidesToSlide: 1,
    position: "none",
  },
  tablet: {
    breakpoint: { max: 1024, min: 464 },
    items: 2,
    slidesToSlide: 2,
  },
  mobile: {
    breakpoint: { max: 464, min: 0 },
    items: 1,
    slidesToSlide: 1,
  },
};

const Container = styled("div")(({ theme }) => ({
  minHeight: "auto",
  width: "100%",
  backgroundColor: "#deedf5",
  padding: 20,
  paddingBottom: 70, // ← add bottom padding so content never hides behind button
  boxSizing: "border-box",
  position: "relative",
}));

// FIX 1: DateBox now uses marginBottom: 12 (was 30) for tighter top section
const DateBox = styled("div")(({ theme }) => ({
  display: "flex",
  alignItems: "center",
  marginBottom: "12px",
}));

const CompanyBox = styled(Box)(({ theme }) => ({
  height: "62vh",
  width: "100%",
  maxWidth: "300px",
  backgroundColor: "white",
  borderRadius: 8,
  paddingTop: 12,
  boxSizing: "border-box",
  overflowY: "auto",
  margin: 0,
  [theme.breakpoints.down("sm")]: {
    height: "40vh",
    maxWidth: "100%",
    margin: "0 auto",
  },
}));

const StyledCancelIcon = styled(CancelIcon)(({ theme }) => ({
  marginRight: 16,
}));

export const DateFilter = ({
  searchValue,
  setSearchValue,
  setOpen,
  currentCompany,
  tenantString: tenantStringProp,
  onSearchSuccess,
}) => {
  const navigate = useNavigate();
  const cookies = new Cookies();
  const defaultcompany = cookies.get("defaultcompany");
  const defaulttenant = cookies.get("defaulttenant");

  const selectedCompanyId = currentCompany || defaultcompany;

  let tenantString = tenantStringProp || "";
  if (!tenantString && defaulttenant) {
    tenantString = `&defaulttenant=${defaulttenant}`;
  }

  const [isAutoSubmitted] = useState(() => {
    const searchParams = new URLSearchParams(window.location.search);
    return searchParams.get("autoSubmit") === "true";
  });

  const [values, setValues] = useState(() => {
    const today = new Date();
    const oneYearAgo = new Date();
    oneYearAgo.setFullYear(today.getFullYear() - 1);
    
    const getFormattedDate = (date) => {
      const yyyy = date.getFullYear();
      const mm = String(date.getMonth() + 1).padStart(2, "0");
      const dd = String(date.getDate()).padStart(2, "0");
      return `${yyyy}-${mm}-${dd}`;
    };

    const defaultDateTo = getFormattedDate(today);
    const defaultDateFrom = getFormattedDate(oneYearAgo);
    
    const scannedToggleCookie = cookies.get("scanned_date_toggle");
    const initialIsScannedDate = scannedToggleCookie !== undefined ? scannedToggleCookie === "true" : false;

    return {
      isDocumentDate: true,
      isScannedDate: initialIsScannedDate,
      documentDateFrom: defaultDateFrom,
      documentDateTo: defaultDateTo,
      scannedDateFrom: defaultDateFrom,
      scannedDateTo: defaultDateTo,
      selectedCompanies: selectedCompanyId
        ? [parseInt(selectedCompanyId, 10) || selectedCompanyId]
        : [],
    };
  });

  const [lockCategories, setLockCategories] = useState(false);
  const [selectedCategories, setSelectedCategories] = useState({});
  const [searchText, setSearchText] = useState(searchValue);

  const handleLockCategoriesChange = () => {
    const newLock = !lockCategories;
    setLockCategories(newLock);

    if (newLock) {
      const isServiceSelectedInAny = values?.selectedCompanies?.some((companyId) =>
        selectedCategories?.[companyId]?.includes("Service")
      );

      if (isServiceSelectedInAny) {
        let copyValue = { ...selectedCategories };
        values?.selectedCompanies?.forEach((companyId) => {
          const isCatExistForCompany =
            groupByCategory?.[companyId]?.categories?.includes("Service");
          if (isCatExistForCompany) {
            if (copyValue?.[companyId]) {
              const isCatAlreadyExist = copyValue?.[companyId]?.some(
                (item) => item === "Service",
              );
              if (!isCatAlreadyExist) {
                copyValue = {
                  ...copyValue,
                  [companyId]: [...copyValue?.[companyId], "Service"],
                };
              }
            } else {
              copyValue = {
                ...copyValue,
                [companyId]: ["Service"],
              };
            }
          }
        });
        setSelectedCategories(copyValue);
      }
    }
  };
  const [companySearch, setCompanySearch] = useState("");
  const [categorySearch, setCategorySearch] = useState("");

  const { data: allCompaniesWithCategory } = useQuery({
    queryKey: ["allUsersWithCategory", selectedCompanyId, tenantString],
    queryFn: () =>
      getWithQueryApiServices(
        apiRoutes.GET_ALL_USER_WITH_CATEGORY,
        `pageNumber=1&selectedCompany=${selectedCompanyId}${tenantString}`,
      ),
  });

  const groupByCategory = allCompaniesWithCategory?.data?.reduce((obj, cat) => {
    const { companyID } = cat;
    obj[companyID] = cat;
    return obj;
  }, {});

  // Parse company from URL and update selectedCompanies if specified
  useEffect(() => {
    if (allCompaniesWithCategory?.data) {
      const searchParams = new URLSearchParams(window.location.search);
      const urlCompany = searchParams.get("company");
      const hasAutoSubmit = searchParams.get("autoSubmit") === "true";
      if (urlCompany) {
        const matchedCompany = allCompaniesWithCategory.data.find(
          (c) => c.name.toLowerCase() === decodeURIComponent(urlCompany).toLowerCase()
        );
        if (matchedCompany) {
          const compId = matchedCompany.companyID;
          if (!values.selectedCompanies.includes(compId)) {
            setValues((prev) => ({
              ...prev,
              selectedCompanies: [compId]
            }));
          }
        } else if (hasAutoSubmit && values.selectedCompanies.length === 0 && allCompaniesWithCategory.data.length > 0) {
          // Fallback: If URL company is invalid, select the first available company
          setValues((prev) => ({
            ...prev,
            selectedCompanies: [allCompaniesWithCategory.data[0].companyID]
          }));
        }
      } else if (hasAutoSubmit && values.selectedCompanies.length === 0 && allCompaniesWithCategory.data.length > 0) {
        // Fallback: If no company in URL, select the first available company
        setValues((prev) => ({
          ...prev,
          selectedCompanies: [allCompaniesWithCategory.data[0].companyID]
        }));
      }
    }
  }, [allCompaniesWithCategory, values.selectedCompanies]);

  useEffect(() => {
    if (
      selectedCompanyId &&
      groupByCategory &&
      values.selectedCompanies.length > 0
    ) {
      const companyId = values.selectedCompanies[0] || parseInt(selectedCompanyId, 10) || selectedCompanyId;
      const companyData = groupByCategory[companyId];

      if (companyData?.categories) {
        const searchParams = new URLSearchParams(window.location.search);
        const urlCategory = searchParams.get("category");
        const hasAutoSubmit = searchParams.get("autoSubmit") === "true";

        if (urlCategory) {
          if (!selectedCategories[companyId]) {
            const matchedCat = companyData.categories.find(
              (c) => c.toLowerCase() === decodeURIComponent(urlCategory).toLowerCase()
            );
            if (matchedCat) {
              setSelectedCategories({ [companyId]: [matchedCat] });
            } else if (hasAutoSubmit && companyData.categories.length > 0) {
              // Fallback: If URL category is not matched, select all categories to prevent search from getting stuck
              setSelectedCategories({ [companyId]: [...companyData.categories] });
            }
          }
        } else if (hasAutoSubmit && (!selectedCategories[companyId] || selectedCategories[companyId].length === 0) && companyData.categories.length > 0) {
          // If autoSubmit is requested but no category is specified, select all categories for this company to run the search successfully
          setSelectedCategories({ [companyId]: [...companyData.categories] });
        }
      }
    }
  }, [selectedCompanyId, groupByCategory, values.selectedCompanies]);

  // Auto-submit search if autoSubmit=true is present in the URL query string
  useEffect(() => {
    const searchParams = new URLSearchParams(window.location.search);
    const hasAutoSubmit = searchParams.get("autoSubmit") === "true";
    const hasQuery =
      searchParams.get("q") && searchParams.get("q").trim().length > 0;
    if (hasAutoSubmit && hasQuery && Object.keys(selectedCategories).length > 0) {
      const timer = setTimeout(() => {
        // Remove autoSubmit and startTour from the URL so page refreshes don't re-trigger it
        const url = new URL(window.location.href);
        url.searchParams.delete("autoSubmit");
        url.searchParams.delete("startTour");
        window.history.replaceState({}, "", url.toString());

        handleSubmit();
      }, 500);
      return () => clearTimeout(timer);
    }
  }, [selectedCategories]);

  const activateScannedDateIfNeeded = () => {
    if (!values?.isScannedDate) {
      cookies.set("scanned_date_toggle", "true", { path: "/" });
      setValues((prev) => ({
        ...prev,
        isScannedDate: true,
      }));
    }
  };

  const handleChange = (name, value) => {
    let nextValues = {
      ...values,
      [name]: value,
    };
    if (name.startsWith("scannedDate") && !values?.isScannedDate) {
      cookies.set("scanned_date_toggle", "true", { path: "/" });
      nextValues.isScannedDate = true;
    }
    setValues(nextValues);
  };

  const handleCompanyCheckBox = (companyId) => {
    let copyValue = [...values?.selectedCompanies];
    const isCompanyExist = copyValue?.some((id) => id === companyId);

    if (isCompanyExist) {
      copyValue = copyValue?.filter((item) => item !== companyId);
    } else {
      copyValue.push(companyId);
    }
    setValues({ ...values, selectedCompanies: copyValue });

    if (selectedCategories?.[companyId]) {
      let categories = { ...selectedCategories };
      delete categories?.[companyId];
      setSelectedCategories({ ...categories });
    }
  };

  const isCategoryLocked = (category, value) => {
    let copyValue = { ...selectedCategories };

    values?.selectedCompanies?.forEach((companyId) => {
      const isCatExistForCompany =
        groupByCategory?.[companyId]?.categories?.includes(category);
      if (isCatExistForCompany) {
        if (copyValue?.[companyId]) {
          const isCatAlreadyExist = copyValue?.[companyId]?.some(
            (item) => item === category,
          );
          let removeDuplicates = [];
          if (value) {
            if (!isCatAlreadyExist) {
              removeDuplicates = [...copyValue?.[companyId], category];
            } else {
              removeDuplicates = [...copyValue?.[companyId]];
            }
          } else {
            if (isCatAlreadyExist) {
              removeDuplicates = copyValue?.[companyId]?.filter(
                (item) => item !== category,
              );
            } else {
              removeDuplicates = [...copyValue?.[companyId]];
            }
          }

          copyValue = {
            ...copyValue,
            [companyId]: removeDuplicates,
          };
        } else {
          if (value) {
            copyValue = {
              ...copyValue,
              [companyId]: [category],
            };
          }
        }
      }
    });

    setSelectedCategories({
      ...copyValue,
    });
  };

  const categoryOnchange = (companyId, category, value) => {
    if (lockCategories) {
      return isCategoryLocked(category, value);
    }
    let copyValue = { ...selectedCategories };

    if (copyValue?.[companyId]) {
      let removeDuplicates = copyValue?.[companyId]?.some(
        (item) => item === category,
      )
        ? copyValue?.[companyId]?.filter((item) => item !== category)
        : [...copyValue?.[companyId], category];

      copyValue = {
        ...copyValue,
        [companyId]: removeDuplicates,
      };
    } else {
      copyValue = {
        ...copyValue,
        [companyId]: [category],
      };
    }

    setSelectedCategories({
      ...copyValue,
    });
  };

  const handleSelectAllCategories = (companyId, checked) => {
    const cats = groupByCategory?.[companyId]?.categories || [];
    
    if (lockCategories) {
      let copyValue = { ...selectedCategories };
      values?.selectedCompanies?.forEach((compId) => {
        const compCats = groupByCategory?.[compId]?.categories || [];
        if (checked) {
          copyValue[compId] = [...compCats];
        } else {
          copyValue[compId] = [];
        }
      });
      setSelectedCategories(copyValue);
      return;
    }

    let copyValue = { ...selectedCategories };
    if (checked) {
      copyValue = {
        ...copyValue,
        [companyId]: [...cats]
      };
    } else {
      copyValue = {
        ...copyValue,
        [companyId]: []
      };
    }
    setSelectedCategories(copyValue);
  };

  const clearSearch = () => {
    setSearchText("");
    setSearchValue("");
  };

  const handleSubmit = () => {
    let allCat = [];
    Object.values(selectedCategories)?.forEach((item) => {
      allCat = [...allCat, ...item];
    });

    if (searchText && searchText.trim().length > 0 && searchText.trim().length < 1) {
      return errorToast("Search text must be at least 1 character");
    }
    if (
      values?.isDocumentDate &&
      (!values?.documentDateFrom || !values?.documentDateTo)
    ) {
      return errorToast("Select Documents From and To date");
    }
    if (
      values?.isScannedDate &&
      (!values?.scannedDateFrom || !values?.scannedDateTo)
    ) {
      return errorToast("Select Scanned From and To date");
    }
    if (values?.selectedCompanies?.length === 0 || allCat?.length === 0) {
      return errorToast("Select any Company/Categories");
    }

    let workData = {
      searchQuery: searchText || "",
      selectedCompanies: values?.selectedCompanies,
      selectedCategories: [...new Set([...allCat])],
      pageNumber: 1,
      pageSize: 20,
    };

    if (
      values?.isDocumentDate &&
      values?.documentDateFrom &&
      values?.documentDateTo
    ) {
      workData.documentDateFrom = values?.documentDateFrom;
      workData.documentDateTo = values?.documentDateTo;
    }
    if (
      values?.isScannedDate &&
      values?.scannedDateFrom &&
      values?.scannedDateTo
    ) {
      workData.scannedDateFrom = values?.scannedDateFrom;
      workData.scannedDateTo = values?.scannedDateTo;
    }

    setSearchValue(searchText);
    getPowerSearch.mutate(workData);
  };

  const getPowerSearch = useMutation({
    mutationKey: ["powerSearch"],
    mutationFn: (wData) =>
      postWithQueryApiServices(
        apiRoutes.POWER_SEARCH,
        `selectedCompany=${selectedCompanyId}${tenantString}`,
        wData,
      ),
    onMutate: (wData) => {
      let newWindow = null;
      if (!isAutoSubmitted) {
        newWindow = window.open('about:blank', '_blank');
        if (newWindow) {
          newWindow.document.write(`
            <html>
              <head>
                <title>Searching...</title>
                <link rel="preconnect" href="https://fonts.googleapis.com">
                <link rel="preconnect" href="https://fonts.gstatic.com" crossorigin>
                <link href="https://fonts.googleapis.com/css2?family=Poppins:wght@500&display=swap" rel="stylesheet">
                <style>
                  body {
                    margin: 0;
                    height: 100vh;
                    display: flex;
                    flex-direction: column;
                    justify-content: center;
                    align-items: center;
                    font-family: 'Poppins', sans-serif;
                    background-color: #7285b3;
                    color: #fff;
                  }
                </style>
              </head>
              <body>
                <div style="font-weight: 500; font-size: 1.25rem; letter-spacing: 0.5px;">Searching...</div>
              </body>
            </html>
          `);
          newWindow.document.close();
        }
      }
      return { newWindow };
    },
    onSuccess: ({ data }, variables, context) => {
      const searchId = Date.now().toString();
      const searchState = {
        data: data?.results || [],
        totalCount: data?.totalCount || 0,
        searchValue: searchText,
        workData: variables,
        searchType: "power",
        selectedCompanyId,
        tenantString
      };
      localStorage.setItem(`search_${searchId}`, JSON.stringify(searchState));

      if (isAutoSubmitted) {
        navigate(`/searchResult?searchId=${searchId}`);
        setOpen && setOpen(false);
        onSearchSuccess && onSearchSuccess();
      } else {
        if (context?.newWindow) {
          context.newWindow.location.href = `/searchResult?searchId=${searchId}&hideHeader=true`;
        } else {
          window.open(`/searchResult?searchId=${searchId}&hideHeader=true`, "_blank");
        }
      }
    },
    onError: (err, variables, context) => {
      if (context?.newWindow) {
        context.newWindow.close();
      }
      errorToast("Failed to perform search");
    }
  });

  const isCategoryChecked = (companyId, category) => {
    const val = selectedCategories?.[companyId]?.includes(category);
    return val ? true : false;
  };


  return (
    <Container>
      {getPowerSearch.isPending && (
        <Box
          sx={{
            position: 'absolute',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            backgroundColor: 'rgba(255, 255, 255, 0.75)',
            zIndex: 9999,
            display: 'flex',
            flexDirection: 'column',
            justifyContent: 'center',
            alignItems: 'center',
            backdropFilter: 'blur(3px)',
            borderRadius: '8px'
          }}
        >
          <CircularProgress size={50} sx={{ mb: 2 , color: '#7286b1'}} />
          <Typography variant="h6" color="#7286b1" sx={{ fontWeight: 'bold' }}>
            Searching...
          </Typography>
        </Box>
      )}
      <IconButton
        id="tour-advanced-search-close-btn"
        sx={{
          position: "absolute",
          right: { xs: 10, sm: 20 },
          top: { xs: 10, sm: 14 },
        }}
        size="small"
        onClick={() => {
          setSearchText("");
          setSearchValue("");
          setOpen && setOpen(false);
        }}
      >
        <CloseIcon sx={{ fontSize: { xs: "20px", sm: "26px" } }} />
      </IconButton>      <Grid container spacing={3} sx={{ mt: 1 }}>
        {/* ── TOP SECTION: Search bar + Date filters ── */}
        <Grid item xs={12} sx={{ pt: "0px !important" }}>
          <Box
            sx={{
              backgroundColor: "#deedf5",
              paddingTop: "0px !important",
              // paddingBottom: "24px",
              paddingRight: { xs: "0px", md: "48px" },
            }}
          >
            {/* Row 1: Search bar — full width centered */}
            <Box
              id="tour-advanced-search-input"
              sx={{
                display: "flex",
                justifyContent: "center",
                alignItems: "center",
                gap: "12px",
                marginBottom: "16px",
                px: { xs: 1, sm: 0 },
              }}
            >
              <CommonSearch
                style={{ width: "500px", maxWidth: "100%", height: "42px", alignItems: "center", backgroundColor: "white" }}
                label="Search (Optional)"
                value={searchText}
                handleChange={(e) => setSearchText(e.target.value)}
                minChars={3}
              />
              <Button
                id="tour-advanced-search-submit"
                variant="contained"
                onClick={handleSubmit}
                sx={{
                  height: "42px",
                  whiteSpace: "nowrap",
                  px: 3,
                }}
              >
                Perform Search
              </Button>
            </Box>

            {/* Row 2: Both date rows side by side, centered */}
            <Box
              sx={{
                display: "flex",
                justifyContent: "center",
                alignItems: "center",
                gap: "20px",
                flexWrap: "wrap",
                px: { xs: 1, sm: 0 },
              }}
            >
              {/* Document Date */}
              <Box
                id="tour-advanced-search-docdate"
                sx={{
                  display: "flex",
                  alignItems: { xs: "stretch", sm: "center" },
                  flexDirection: { xs: "column", sm: "row" },
                  gap: "8px",
                  backgroundColor: "white",
                  borderRadius: "8px",
                  padding: "8px 14px",
                  boxShadow: "0 1px 3px rgba(0,0,0,0.08)",
                  width: { xs: "100%", sm: "auto" },
                  maxWidth: { xs: "500px", sm: "none" },
                  boxSizing: "border-box",
                }}
              >
                <Box sx={{ display: "flex", alignItems: "center", gap: "8px" }}>
                  <Switch
                    name="isDocumentDate"
                    checked={true}
                    disabled={true}
                    size="small"
                  />
                  <Typography
                    sx={{
                      fontSize: 13,
                      whiteSpace: "nowrap",
                      fontWeight: 600,
                      color: "#333",
                    }}
                  >
                    Document Date
                  </Typography>
                </Box>
                <Box
                  sx={{
                    display: "flex",
                    alignItems: "center",
                    gap: "8px",
                    width: "100%",
                    flexDirection: { xs: "column", sm: "row" },
                  }}
                >
                  <Box sx={{ display: "flex", alignItems: "center", gap: "8px", width: "100%" }}>
                    <Typography sx={{ fontSize: 12, color: "#888", minWidth: "32px" }}>
                      From
                    </Typography>
                    <CustomTextField
                      type="date"
                      disabled={false}
                      style={{ width: "100%" }}
                      name="documentDateFrom"
                      value={values?.documentDateFrom}
                      onChange={(e) => handleChange(e.target.name, e.target.value)}
                      max={values?.documentDateTo}
                    />
                  </Box>
                  <Box sx={{ display: "flex", alignItems: "center", gap: "8px", width: "100%" }}>
                    <Typography sx={{ fontSize: 12, color: "#888", minWidth: "32px" }}>
                      To
                    </Typography>
                    <CustomTextField
                      type="date"
                      disabled={false}
                      style={{ width: "100%" }}
                      name="documentDateTo"
                      value={values?.documentDateTo}
                      onChange={(e) => handleChange(e.target.name, e.target.value)}
                      min={values?.documentDateFrom}
                    />
                  </Box>
                </Box>
              </Box>

              {/* Scanned Date */}
              <Box
                id="tour-advanced-search-scandate"
                sx={{
                  display: "flex",
                  alignItems: { xs: "stretch", sm: "center" },
                  flexDirection: { xs: "column", sm: "row" },
                  gap: "8px",
                  backgroundColor: "white",
                  borderRadius: "8px",
                  padding: "8px 14px",
                  boxShadow: "0 1px 3px rgba(0,0,0,0.08)",
                  width: { xs: "100%", sm: "auto" },
                  maxWidth: { xs: "500px", sm: "none" },
                  boxSizing: "border-box",
                }}
              >
                <Box sx={{ display: "flex", alignItems: "center", gap: "8px" }}>
                  <Switch
                    name="isScannedDate"
                    checked={!!values?.isScannedDate}
                    onChange={(e) => {
                      const checked = e.target.checked;
                      cookies.set("scanned_date_toggle", checked ? "true" : "false", { path: "/" });
                      setValues({
                        ...values,
                        isScannedDate: checked,
                      });
                    }}
                    onKeyDown={(e) => {
                      if (e.key === "Enter") {
                        e.preventDefault();
                        const nextVal = !values?.isScannedDate;
                        cookies.set("scanned_date_toggle", nextVal ? "true" : "false", { path: "/" });
                        setValues({
                          ...values,
                          isScannedDate: nextVal,
                        });
                      }
                    }}
                    size="small"
                  />
                  <Typography
                    sx={{
                      fontSize: 13,
                      whiteSpace: "nowrap",
                      fontWeight: 600,
                      color: "#333",
                    }}
                  >
                    Scanned Date
                  </Typography>
                </Box>
                <Box
                  sx={{
                    display: "flex",
                    alignItems: "center",
                    gap: "8px",
                    width: "100%",
                    flexDirection: { xs: "column", sm: "row" },
                  }}
                >
                  <Box sx={{ display: "flex", alignItems: "center", gap: "8px", width: "100%" }}>
                    <Typography sx={{ fontSize: 12, color: "#888", minWidth: "32px" }}>
                      From
                    </Typography>
                    <CustomTextField
                      type="date"
                      disabled={false}
                      style={{ width: "100%" }}
                      name="scannedDateFrom"
                      value={values?.scannedDateFrom}
                      onChange={(e) => handleChange(e.target.name, e.target.value)}
                      onFocus={activateScannedDateIfNeeded}
                      onClick={activateScannedDateIfNeeded}
                      max={values?.scannedDateTo}
                    />
                  </Box>
                  <Box sx={{ display: "flex", alignItems: "center", gap: "8px", width: "100%" }}>
                    <Typography sx={{ fontSize: 12, color: "#888", minWidth: "32px" }}>
                      To
                    </Typography>
                    <CustomTextField
                      type="date"
                      disabled={false}
                      style={{ width: "100%" }}
                      name="scannedDateTo"
                      value={values?.scannedDateTo}
                      onChange={(e) => handleChange(e.target.name, e.target.value)}
                      onFocus={activateScannedDateIfNeeded}
                      onClick={activateScannedDateIfNeeded}
                      min={values?.scannedDateFrom}
                    />
                  </Box>
                </Box>
              </Box>
            </Box>
          </Box>
        </Grid>

        {/* Companies panel */}
        <Grid item xs={12} sm={4} md={3} id="tour-advanced-search-companies">
          <Typography sx={{ mb: 1, fontWeight: "bold" }}>
            Companies
          </Typography>
          <Box sx={{ pr: "16px", boxSizing: "border-box" }}>
            <CompanyBox>
              <div style={{}}>
                <CommonSearch
                  style={{ width: "100%", maxWidth: "100%", border: "none", marginBottom: 8 }}
                  label={"Search Company"}
                  value={companySearch}
                  handleChange={(e) => setCompanySearch(e.target.value)}
                  minChars={3}
                />
                {allCompaniesWithCategory?.data
                  ?.filter(
                    (item) =>
                      Number(item?.dst) !== 0 &&
                      (!companySearch ||
                      item?.name
                        ?.toLowerCase()
                        .includes(companySearch.toLowerCase())),
                  )
                  ?.map((item) => (
                    <ListBox key={item?.companyID}>
                      <FormControlLabel
                        control={
                          <Checkbox
                            checked={values?.selectedCompanies?.some(
                              (id) => id === item?.companyID,
                            )}
                            onChange={() =>
                              handleCompanyCheckBox(item?.companyID)
                            }
                            onKeyDown={(e) => {
                              if (e.key === "Enter") {
                                e.preventDefault();
                                handleCompanyCheckBox(item?.companyID);
                              }
                            }}
                          />
                        }
                        label={
                          <Box
                            component="span"
                            sx={{
                              display: "block",
                              width: "100%",
                              maxWidth: "100%",
                              whiteSpace: "nowrap",
                              overflow: "hidden",
                              textOverflow: "ellipsis",
                            }}
                            title={item?.name}
                          >
                            {item?.name}
                          </Box>
                        }
                        sx={{
                          cursor: "pointer",
                          width: "100%",
                          minWidth: 0,
                          pl: "18px",
                          m: 0,
                          '& .MuiFormControlLabel-label': {
                            minWidth: 0,
                            overflow: 'hidden',
                            width: '100%',
                          }
                        }}
                      />
                    </ListBox>
                  ))}
              </div>
            </CompanyBox>
          </Box>
        </Grid>

        {/* Categories panel */}
        <Grid item xs={12} sm={8} md={9} id="tour-advanced-search-categories">
          <Box sx={{ display: "flex", flexWrap: "wrap", alignItems: "center", gap: "8px", mb: 1, pl: { xs: 0, sm: "20px" } }}>
            <Typography style={{ fontWeight: "bold" }}>
              Categories
            </Typography>
            <FormControlLabel
              control={
                <Checkbox
                  checked={lockCategories}
                  onChange={handleLockCategoriesChange}
                  onKeyDown={(e) => {
                    if (e.key === "Enter") {
                      e.preventDefault();
                      handleLockCategoriesChange();
                    }
                  }}
                />
              }
              label={"Lock Categories"}
              style={{
                margin: 0,
                padding: "0",
                paddingLeft: "16px",
              }}
            />
          </Box>
          <Box sx={{ position: "relative", pl: { xs: 0, sm: "20px" } }}>
            {values?.selectedCompanies?.length ? (
              <Carousel responsive={responsive}>
                {values?.selectedCompanies?.map((id, index) => (
                  <Box key={id} sx={{ pr: "16px", boxSizing: "border-box" }}>
                    <CompanyBox>
                      <CommonSearch
                        style={{
                          width: "100%",
                          maxWidth: "100%",
                          border: "none",
                          marginBottom: 8,
                          boxSizing: "border-box",
                        }}
                        label={"Search Category"}
                        value={categorySearch}
                        handleChange={(e) => setCategorySearch(e.target.value)}
                      />

                      {groupByCategory?.[id]?.categories && groupByCategory?.[id]?.categories.length > 0 && (
                        <ListBox key="select-all" style={{ borderBottom: '1px solid #eee', marginBottom: 4 }}>
                          <FormControlLabel
                            control={
                              <Checkbox
                                checked={
                                  groupByCategory?.[id]?.categories.every(cat => 
                                    isCategoryChecked(id, cat)
                                  )
                                }
                                indeterminate={
                                  groupByCategory?.[id]?.categories.some(cat => 
                                    isCategoryChecked(id, cat)
                                  ) && !groupByCategory?.[id]?.categories.every(cat => 
                                    isCategoryChecked(id, cat)
                                  )
                                }
                                onChange={(e) =>
                                  handleSelectAllCategories(id, e.target.checked)
                                }
                              />
                            }
                            label={<strong>Select All</strong>}
                            style={{ paddingLeft: "18px" }}
                          />
                        </ListBox>
                      )}

                      {groupByCategory?.[id]?.categories
                        ?.filter(
                          (category) =>
                            !categorySearch ||
                            category
                              ?.toLowerCase()
                              .includes(categorySearch.toLowerCase()),
                        )
                        ?.map((category) => (
                          <ListBox key={category}>
                            <FormControlLabel
                              control={
                                <Checkbox
                                  checked={isCategoryChecked(id, category)}
                                  onChange={(e) =>
                                    categoryOnchange(
                                      id,
                                      category,
                                      e.target.checked,
                                    )
                                  }
                                  onKeyDown={(e) => {
                                    if (e.key === "Enter") {
                                      e.preventDefault();
                                      categoryOnchange(
                                        id,
                                        category,
                                        !isCategoryChecked(id, category),
                                      );
                                    }
                                  }}
                                />
                              }
                              label={category}
                              style={{ paddingLeft: "18px" }}
                            />
                          </ListBox>
                        ))}
                    </CompanyBox>
                  </Box>
                ))}
              </Carousel>
            ) : (
              <Box
                sx={{
                  display: "flex",
                  justifyContent: "center",
                  alignItems: "center",
                  height: "40vh",
                }}
              >
                <Typography style={{ fontWeight: "bold", fontSize: 20 }}>
                  Select any Company
                </Typography>
              </Box>
            )}
          </Box>
        </Grid>
      </Grid>

      {getPowerSearch.isPending && (
        <Box
          sx={{
            position: "absolute",
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            backgroundColor: "rgba(255, 255, 255, 0.75)",
            zIndex: 9999,
            display: "flex",
            flexDirection: "column",
            justifyContent: "center",
            alignItems: "center",
            backdropFilter: "blur(3px)",
          }}
        >
          <CircularProgress size={50} sx={{ mb: 2, color: "#7286b1" }} />
          <Typography
            variant="h6"
            color="#7286b1"
            sx={{ fontWeight: "bold" }}
          >
            Searching...
          </Typography>
        </Box>
      )}

    </Container>
  );
};
